import { joiResolver } from 'customJoi';
import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import { useMemo, useState, type FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  constructAccountDetailsFormDefaultValues,
  normalizeMaskedInputs,
} from 'utils/settings/settingsHelpers';
import { validationSchema } from './validation';
import { useTranslation } from 'react-i18next';
import useFeatures from 'hooks/useFeatures';
import useUpdateAccountDetails from 'hooks/useUpdateAccountDetails';
import { CheckCircleIcon } from 'utils/icons';
import { SizesEnum } from 'components/atoms/Title/Title.enum';
import { AccountSettingsFieldsNamesEnum } from 'types/settings.types';
import {
  FieldConnector,
  FieldTooltip,
} from 'components/atoms/Form/FieldConnector';
import FreeTextInput from 'components/atoms/Form/FreeTextInput';
import { Title } from 'components/atoms/Title';
import { Button } from 'components/atoms/Button';
import type { IUser } from 'types/users.types';
import DatePicker from 'components/atoms/DatePicker/DatePicker.component';
import dayjs from 'dayjs';
import {
  ProfileUserCancelCloseIcon,
  ProfileUserIcon,
} from 'utils/icons/figmaIcons';
import CalendarAppointmentBoldIcon from 'components/atoms/SVG/CalendarAppointmentBoldIcon.component';

const StarterDiscountForm: FC<{ onClose?: () => void }> = ({ onClose }) => {
  const { data: user } = useGetCurrentCustomerData();

  const defaultValues = useMemo(
    () => constructAccountDetailsFormDefaultValues(user as IUser),
    [user],
  );

  const [currentStep, setCurrentStep] = useState(0);

  const methods = useForm({
    defaultValues: defaultValues,
    resolver: joiResolver(validationSchema, {
      allowUnknown: true,
      abortEarly: false,
    }),
  });

  const { t } = useTranslation();

  const vatNumberPlaceholder = useFeatures('settings.vatNumberPlaceholder');

  const settingsFeatures = useFeatures('settings');

  const { VATNumberMask, VATNumberMaskNormalizer } = settingsFeatures;

  const { update, isUpdating } = useUpdateAccountDetails(
    methods.handleSubmit,
    (res) => {
      methods.setValue(
        AccountSettingsFieldsNamesEnum.VATNumber,
        res?.VATNumber,
      );
      methods.setValue(
        AccountSettingsFieldsNamesEnum.startDate,
        res?.startDate ? dayjs(res?.startDate).toDate() : undefined,
      );

      // In case the start date was pre-existing by KBO, skip the start date
      // form and go directly to the results step
      if (currentStep === 0 && typeof res?.startDate === 'string') {
        return setCurrentStep((step) => step + 2);
      }

      return setCurrentStep((step) => step + 1);
    },
  );

  const isEligibleForDiscount = useMemo(
    () =>
      user?.VATNumber &&
      user?.startDate &&
      dayjs().diff(dayjs(user?.startDate), 'day') < 365,
    [user?.VATNumber, user?.startDate],
  );

  const steps = [
    {
      title: t('discounts.starter_discount.discount_form.vat_title'),
      description: t(
        'discounts.starter_discount.discount_form.vat_description',
      ),
      icon: <ProfileUserIcon className="w-10 text-taxes" />,
      content: (
        <FieldConnector
          key="vat-number-field"
          className="mt-3"
          destroyOnHide={false}
          name={AccountSettingsFieldsNamesEnum.VATNumber}
          label={t('settings.companyDetails.vatNumber')}
          helper={
            <FieldTooltip
              content={t(`settings.companyDetails.vat_number_tooltip.be`)}
            />
          }
        >
          {({ field, name }) => (
            <FreeTextInput
              placeholder={vatNumberPlaceholder}
              name={name}
              type="maskedInput"
              maskProps={{
                maskChar: null,
                mask: VATNumberMask,
                normalizer: VATNumberMaskNormalizer,
              }}
              value={field.value}
              onChange={({ target: { value: inputValue } }) =>
                field.onChange(normalizeMaskedInputs(inputValue))
              }
            />
          )}
        </FieldConnector>
      ),
    },
    {
      title: t('discounts.starter_discount.discount_form.start_date_title'),
      description: t(
        'discounts.starter_discount.discount_form.start_date_description',
      ),
      icon: <CalendarAppointmentBoldIcon className="w-10 text-primary" />,
      content: (
        <FieldConnector
          key="start-date-field"
          className="mt-3"
          destroyOnHide={false}
          name={AccountSettingsFieldsNamesEnum.startDate}
          label={t('settings.companyDetails.startActivityDate')}
        >
          {({ field }) => (
            <DatePicker
              selected={field.value ? dayjs(field.value).toDate() : null}
              scrollableYearDropdown
              showYearDropdown
              onChange={(value) => field.onChange(value)}
              placeholder={`${dayjs().format('DD/MM/YYYY')}`}
            />
          )}
        </FieldConnector>
      ),
    },
    {
      title: isEligibleForDiscount
        ? t('discounts.starter_discount.discount_form.qualified_title')
        : t('discounts.starter_discount.discount_form.unqualified_title'),
      description: isEligibleForDiscount
        ? t('discounts.starter_discount.discount_form.qualified_description')
        : t('discounts.starter_discount.discount_form.unqualified_description'),
      icon: isEligibleForDiscount ? (
        <CheckCircleIcon className="w-12 h-12 text-taxes" />
      ) : (
        <ProfileUserCancelCloseIcon className="w-10 text-taxes" />
      ),
      content: null,
    },
  ];

  const isLastStep = currentStep === steps.length - 1;

  return (
    <FormProvider {...methods}>
      <form onSubmit={update}>
        <div className="flex flex-col items-center">
          <div className="mb-1">{steps[currentStep].icon}</div>

          <Title type={SizesEnum.h6}>{steps[currentStep].title}</Title>

          <p className="text-primary-600 text-center">
            {steps[currentStep].description}
          </p>
        </div>

        {steps[currentStep].content ? (
          <div key={currentStep} className="">
            {steps[currentStep].content}
          </div>
        ) : null}

        {isLastStep ? null : (
          <div className="text-center w-full mt-8 flex items-center gap-4">
            <Button
              type="button"
              color="taxes"
              onClick={onClose}
              structure="light"
              className="w-full"
            >
              {t('cancel')}
            </Button>

            <Button
              type="submit"
              color="taxes"
              loading={isUpdating}
              className="w-full"
            >
              {t('settings.controllers.save')}
            </Button>
          </div>
        )}
      </form>
    </FormProvider>
  );
};

export default StarterDiscountForm;
